import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import MFAMethodButton, { MFAMethodType } from '../../../components/common/MfaButton';
import LoaderButton from '../../../components/common/LoaderButton';
import lockGlowIcon from '../../../assets/images/icons/lock-glow.svg';
import MfaEmailIcon from '../../../assets/images/icons/mfa-email-img.png';
import MfaPhoneIcon from '../../../assets/images/icons/mfa-phone-img.png';
import alert from '../../../utils/alert';

import { useAppDispatch, useAppSelector } from '../../../store';
import {
  generateMfaCodeThunk,
  getMfaPreferenceThunk,
  selectMfaState,
  updateMfaPreferenceThunk,
} from '../api/mfaSlice';

const MfaPreference = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector(selectMfaState);

  const [selectedMethod, setSelectedMethod] = useState<MFAMethodType | null>(null);

  const [isPreferenceUpdated, setIsPreferenceUpdated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  useEffect(() => {
    const fetchPreference = async () => {
      try {
        const result = await dispatch(getMfaPreferenceThunk(email)).unwrap();
        if (result.resource) {
          setSelectedMethod(result.resource);
          setIsPreferenceUpdated(true);
        } else {
          setSelectedMethod('sms');
        }
      } catch (error: any) {
        alert.error(t('Something went wrong. Please try again.'));
      }
    };

    fetchPreference();
  }, [dispatch, email, t]);

  const onSelectMethod = useCallback(
    async (method: MFAMethodType) => {
      setIsPreferenceUpdated(false);

      try {
        const result = await dispatch(
          updateMfaPreferenceThunk({ email: email, mfaType: method }),
        ).unwrap();

        if (result.statusCode === 200) {
          setSelectedMethod(method);
          setIsPreferenceUpdated(true);
        } else {
          alert.error(t('Failed to update verification method. Please try again.'));
        }
      } catch (error: any) {
        if (error.message == 'Resource not found.') {
          alert.error(t('Unable to verify user information. Please try again later.'));
        } else {
          alert.error(t('Something went wrong. Please try again.'));
        }
      }
    },
    [dispatch, email, t],
  );

  const onSendCode = useCallback(async () => {
    if (!isPreferenceUpdated) {
      alert.error(t('Please select a verification method first'));
      return;
    }

    setIsSubmitting(true);
    try {
      dispatch(generateMfaCodeThunk(email));
      navigate('/auth/mfa-verification', { state: { email }, replace: true });
    } catch (error: any) {
      let message = error.error || 'Something went wrong. Please try again.';
      if (error instanceof Error) message = error.message;
      alert.error(t(message), { toastId: 'login-error' });
      navigate('/auth/login');
    } finally {
      setIsSubmitting(false);
    }
  }, [isPreferenceUpdated, dispatch, email, navigate, t]);

  return (
    <div className='d-flex flex-column justify-content-center m-0'>
      <div className='d-flex flex-column'>
        <img className='auth__page-icon' alt={'lock-icon'} src={lockGlowIcon} />
        <p className='auth__page-title'>{t("Let's verify your account")}</p>
        <p className='auth__help-text'>{t('How would you like to receive your code?')}</p>
      </div>
      <div className='auth__form--width'>
        <MFAMethodButton
          method='sms'
          label={t('Text Message')}
          isSelected={selectedMethod === 'sms'}
          isMethodUpdating={isLoading}
          onSelect={onSelectMethod}
          icon={MfaPhoneIcon}
          showUnselectedState={false}
        />
        <MFAMethodButton
          method='email'
          label={t('Email')}
          isSelected={selectedMethod === 'email'}
          isMethodUpdating={isLoading}
          onSelect={onSelectMethod}
          icon={MfaEmailIcon}
          showUnselectedState={false}
        />

        <LoaderButton
          className={`w-100 mt-4`}
          type='submit'
          onClick={onSendCode}
          isSubmitting={isSubmitting}
          disabled={!isPreferenceUpdated || isLoading || isSubmitting}
        >
          {t('Send my code')}
        </LoaderButton>
      </div>
    </div>
  );
};

export default MfaPreference;
